import { useRef, useState, useCallback, useEffect } from "react";
import { MoveHorizontal, ChevronLeft, ChevronRight } from "lucide-react";
import { BEFORE_AFTER } from "../../data";
import { Reveal } from "./Reveal";

export default function BeforeAfter() {
  const [pos, setPos] = useState(50);
  const [idx, setIdx] = useState(0);
  const [w, setW] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const ref = useRef(null);
  const dragging = useRef(false);
  const pair = BEFORE_AFTER[idx];
  const zoom = isMobile && pair.zoomMobile ? pair.zoomMobile : 1;

  useEffect(() => {
    const measure = () => ref.current && setW(ref.current.getBoundingClientRect().width);
    measure();
    const mq = window.matchMedia("(max-width: 768px)");
    const onMq = () => setIsMobile(mq.matches);
    onMq();
    mq.addEventListener("change", onMq);
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("resize", measure);
      mq.removeEventListener("change", onMq);
    };
  }, []);

  const move = useCallback((clientX) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, p)));
  }, []);

  const onDown = () => (dragging.current = true);
  const onUp = () => (dragging.current = false);
  const onMove = (e) => {
    if (!dragging.current) return;
    move(e.touches ? e.touches[0].clientX : e.clientX);
  };
  const go = (d) => { setIdx((idx + d + BEFORE_AFTER.length) % BEFORE_AFTER.length); setPos(50); };

  return (
    <section className="relative bg-white pt-8 pb-24 md:pb-36" data-testid="before-after">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
          <Reveal>
            <p className="text-xs md:text-sm tracking-[0.25em] uppercase text-royal font-semibold mb-5">Antes & Depois</p>
            <h2 className="font-display font-extrabold text-ink tracking-tight text-4xl md:text-6xl leading-[1.02]">
              A diferença<br />arrasta-se ao vivo.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-muted-foreground md:text-lg max-w-sm">
              Arraste a linha e veja o antes e o depois do mesmo vidro. Sem edições, apenas trabalho.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div className="relative max-w-4xl mx-auto">
            <div
              ref={ref}
              className="relative w-full aspect-[3/4] sm:aspect-[4/3] md:aspect-[16/10] overflow-hidden rounded-[1.5rem] shadow-[0_30px_80px_rgba(0,47,171,0.15)] select-none cursor-ew-resize touch-none"
              onMouseDown={onDown}
              onMouseUp={onUp}
              onMouseLeave={onUp}
              onMouseMove={onMove}
              onTouchStart={onDown}
              onTouchEnd={onUp}
              onTouchMove={onMove}
              data-testid="before-after-slider"
            >
              <img src={pair.after} alt="Depois — vidro limpo" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover" style={{ transform: `scale(${zoom})` }} draggable={false} />
              <span className="absolute bottom-4 right-4 z-10 rounded-full bg-royal/90 text-white text-xs font-semibold tracking-wide px-4 py-1.5">DEPOIS</span>

              <div className="absolute inset-y-0 left-0 overflow-hidden" style={{ width: `${pos}%` }}>
                <img src={pair.before} alt="Antes — vidro sujo" loading="lazy" decoding="async" className="absolute inset-y-0 left-0 h-full object-cover max-w-none" style={{ width: w || "100%", transform: `scale(${zoom})` }} draggable={false} />
                <span className="absolute bottom-4 left-4 z-10 rounded-full bg-ink/80 text-white text-xs font-semibold tracking-wide px-4 py-1.5">ANTES</span>
              </div>

              <div className="absolute top-0 bottom-0 z-20 w-0.5 bg-white shadow-[0_0_10px_rgba(0,0,0,0.3)]" style={{ left: `${pos}%` }}>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 grid place-items-center h-12 w-12 rounded-full bg-white text-royal shadow-lg">
                  <MoveHorizontal size={22} />
                </div>
              </div>
            </div>

            {BEFORE_AFTER.length > 1 && (
              <div className="mt-6 flex items-center justify-center gap-6">
                <button onClick={() => go(-1)} className="grid place-items-center h-11 w-11 rounded-full border border-ink/15 hover:border-royal hover:text-royal transition-colors" data-testid="ba-prev" aria-label="Anterior">
                  <ChevronLeft size={20} />
                </button>
                <div className="flex items-center gap-3">
                  {BEFORE_AFTER.map((p, i) => (
                    <button key={i} onClick={() => { setIdx(i); setPos(50); }} className={`text-sm font-medium transition-colors ${i === idx ? "text-royal" : "text-ink/40 hover:text-ink"}`} data-testid={`ba-dot-${i}`}>
                      {p.label}
                    </button>
                  ))}
                </div>
                <button onClick={() => go(1)} className="grid place-items-center h-11 w-11 rounded-full border border-ink/15 hover:border-royal hover:text-royal transition-colors" data-testid="ba-next" aria-label="Seguinte">
                  <ChevronRight size={20} />
                </button>
              </div>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
